/**
 * Interactive Natal Chart Wheel Component
 * Renders the astrological chart using D3.js
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import * as d3 from 'd3';
import type { NatalChart, PlanetPosition, Aspect } from '../../types';
import { ZODIAC_SIGNS, PLANET_SYMBOLS, ASPECT_SYMBOLS } from '../../types';
import { useTelegramApp } from '../../hooks';
import styles from './ChartWheel.module.css';

interface ChartWheelProps {
  chart: NatalChart;
  size?: number;
  onPlanetClick?: (planet: PlanetPosition) => void;
  onAspectClick?: (aspect: Aspect) => void;
}

// Colors for chart elements - более мягкая палитра
const ELEMENT_COLORS: Record<string, string> = {
  fire: '#ff6b6b',    // Мягкий красный
  earth: '#51cf66',   // Мягкий зеленый
  air: '#ffd43b',     // Мягкий желтый
  water: '#74c0fc',   // Мягкий голубой
};

const ELEMENT_GRADIENTS: Record<string, [string, string]> = {
  fire: ['#ff6b6b', '#ff8787'],
  earth: ['#51cf66', '#69db7c'],
  air: ['#ffd43b', '#ffe066'],
  water: ['#74c0fc', '#91d7ff'],
};

const ASPECT_COLORS: Record<string, string> = {
  conjunction: '#cc5de8',  // Фиолетовый
  opposition: '#ff6b6b',   // Красный
  trine: '#51cf66',        // Зеленый
  square: '#ff8787',       // Светло-красный
  sextile: '#74c0fc',      // Голубой
};

// Roman numerals for house labels
const ROMAN_NUMERALS = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'XI', 'XII'];

// Format degrees as °'"
const formatDMS = (decimal: number): string => {
  const degrees = Math.floor(decimal);
  const minutesDecimal = (decimal - degrees) * 60;
  const minutes = Math.floor(minutesDecimal);
  const seconds = Math.floor((minutesDecimal - minutes) * 60);
  return `${degrees}°${minutes.toString().padStart(2, '0')}'${seconds.toString().padStart(2, '0')}"`;
};

// Format speed as °'"/day
const formatSpeed = (speed: number): string => {
  const absSpeed = Math.abs(speed);
  const degrees = Math.floor(absSpeed);
  const minutesDecimal = (absSpeed - degrees) * 60;
  const minutes = Math.floor(minutesDecimal);
  const seconds = Math.floor((minutesDecimal - minutes) * 60);
  const sign = speed < 0 ? '-' : '';
  return `${sign}${degrees}°${minutes}'${seconds}"/день`;
};

// Element for each sign
const SIGN_ELEMENTS: Record<string, string> = {
  aries: 'fire',
  leo: 'fire',
  sagittarius: 'fire',
  taurus: 'earth',
  virgo: 'earth',
  capricorn: 'earth',
  gemini: 'air',
  libra: 'air',
  aquarius: 'air',
  cancer: 'water',
  scorpio: 'water',
  pisces: 'water',
};

export function ChartWheel({
  chart,
  size = 350,
  onPlanetClick,
  onAspectClick,
}: ChartWheelProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [computedSize, setComputedSize] = useState(size);
  const [tooltip, setTooltip] = useState<{
    content: string;
    x: number;
    y: number;
  } | null>(null);
  const { haptic } = useTelegramApp();

  // Responsive size calculation
  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.clientWidth;
        setComputedSize(Math.min(containerWidth, size));
      }
    };

    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, [size]);

  // Convert longitude to chart angle (0° Aries at left, counterclockwise)
  const longitudeToAngle = useCallback((longitude: number) => {
    // Adjust for Ascendant being at 9 o'clock position
    const ascLongitude = chart.points.asc?.longitude || 0;
    return (longitude - ascLongitude + 180) % 360;
  }, [chart.points.asc?.longitude]);

  // Draw the chart
  useEffect(() => {
    if (!svgRef.current || computedSize === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const center = computedSize / 2;
    const outerRadius = computedSize / 2 - 10;
    const zodiacWidth = 30;
    const houseRadius = outerRadius - zodiacWidth;
    const planetRadius = houseRadius - 25;
    const aspectRadius = planetRadius - 40;
    const innerRadius = aspectRadius - 20;

    // Create main group
    const g = svg
      .attr('width', computedSize)
      .attr('height', computedSize)
      .append('g')
      .attr('transform', `translate(${center}, ${center})`);

    // Create gradients for zodiac signs
    const defs = svg.append('defs');
    Object.entries(ELEMENT_GRADIENTS).forEach(([element, [start, end]]) => {
      const gradient = defs.append('radialGradient')
        .attr('id', `gradient-${element}`)
        .attr('cx', '50%')
        .attr('cy', '50%')
        .attr('r', '50%');

      gradient.append('stop')
        .attr('offset', '0%')
        .attr('stop-color', start)
        .attr('stop-opacity', 0.4);

      gradient.append('stop')
        .attr('offset', '100%')
        .attr('stop-color', end)
        .attr('stop-opacity', 0.2);
    });

    // Draw zodiac wheel
    const zodiacArc = d3.arc<d3.PieArcDatum<{ sign: string }>>()
      .innerRadius(houseRadius)
      .outerRadius(outerRadius);

    const pieData = d3.pie<{ sign: string }>()
      .value(() => 1)
      .sort(null)
      .startAngle(-Math.PI / 2)
      .endAngle(Math.PI * 1.5)(
      ZODIAC_SIGNS.map((s) => ({ sign: s.name }))
    );

    g.selectAll('.zodiac-segment')
      .data(pieData)
      .enter()
      .append('path')
      .attr('class', 'zodiac-segment')
      .attr('d', zodiacArc as never)
      .attr('fill', (d) => {
        const element = SIGN_ELEMENTS[d.data.sign];
        return element ? `url(#gradient-${element})` : 'var(--tg-theme-secondary-bg-color)';
      })
      .attr('stroke', 'var(--tg-theme-hint-color)')
      .attr('stroke-width', 0.5)
      .attr('opacity', 1)
      .style('transition', 'all 0.3s ease');

    // Draw zodiac symbols
    g.selectAll('.zodiac-symbol')
      .data(pieData)
      .enter()
      .append('text')
      .attr('class', 'zodiac-symbol')
      .attr('transform', (d) => {
        const angle = (d.startAngle + d.endAngle) / 2;
        const r = (houseRadius + outerRadius) / 2;
        const x = Math.sin(angle) * r;
        const y = -Math.cos(angle) * r;
        return `translate(${x}, ${y})`;
      })
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'central')
      .attr('font-size', '16px')
      .attr('font-weight', '600')
      .attr('fill', (d) => {
        const element = SIGN_ELEMENTS[d.data.sign];
        return element ? ELEMENT_COLORS[element] : 'var(--tg-theme-text-color)';
      })
      .style('text-shadow', '0 1px 2px rgba(0, 0, 0, 0.2)')
      .style('filter', 'drop-shadow(0 0 2px rgba(255, 255, 255, 0.5))')
      .text((_, i) => ZODIAC_SIGNS[i].symbol);

    // Draw house cusps
    Object.values(chart.houses).forEach((house) => {
      const angle = (longitudeToAngle(house.longitude) * Math.PI) / 180;
      const x1 = Math.sin(angle) * innerRadius;
      const y1 = -Math.cos(angle) * innerRadius;
      const x2 = Math.sin(angle) * houseRadius;
      const y2 = -Math.cos(angle) * houseRadius;

      // Extend cusp line through zodiac ring for houses 1, 4, 7, 10
      const isAngularHouse = house.number === 1 || house.number === 4 || house.number === 7 || house.number === 10;
      const x3 = Math.sin(angle) * outerRadius;
      const y3 = -Math.cos(angle) * outerRadius;

      g.append('line')
        .attr('x1', x1)
        .attr('y1', y1)
        .attr('x2', isAngularHouse ? x3 : x2)
        .attr('y2', isAngularHouse ? y3 : y2)
        .attr('stroke', 'var(--tg-theme-hint-color)')
        .attr('stroke-width', house.number === 1 || house.number === 10 ? 2 : 1)
        .attr('opacity', isAngularHouse ? 0.7 : 0.5);

      // House labels outside zodiac ring (Roman numeral + degree)
      const labelR = outerRadius + 12;
      const labelX = Math.sin(angle) * labelR;
      const labelY = -Math.cos(angle) * labelR;
      const houseDegree = Math.floor(house.degree);

      // Calculate rotation for better readability
      const rotationAngle = (angle * 180) / Math.PI;
      // Flip text if on left side of chart
      const shouldFlip = rotationAngle > 90 && rotationAngle < 270;
      const textRotation = shouldFlip ? rotationAngle + 180 : rotationAngle;

      const houseLabel = g.append('text')
        .attr('x', labelX)
        .attr('y', labelY)
        .attr('text-anchor', 'middle')
        .attr('dominant-baseline', 'central')
        .attr('font-size', '9px')
        .attr('font-weight', isAngularHouse ? '600' : '400')
        .attr('fill', isAngularHouse ? 'var(--tg-theme-text-color)' : 'var(--tg-theme-hint-color)')
        .attr('transform', `rotate(${textRotation}, ${labelX}, ${labelY})`);

      houseLabel.append('tspan')
        .text(ROMAN_NUMERALS[house.number - 1]);

      houseLabel.append('tspan')
        .attr('font-size', '7px')
        .attr('baseline-shift', 'super')
        .text(houseDegree.toString());
    });

    // Draw aspects
    chart.aspects.forEach((aspect) => {
      const planet1 = chart.planets[aspect.planet1];
      const planet2 = chart.planets[aspect.planet2];
      if (!planet1 || !planet2) return;

      const angle1 = (longitudeToAngle(planet1.longitude) * Math.PI) / 180;
      const angle2 = (longitudeToAngle(planet2.longitude) * Math.PI) / 180;

      const x1 = Math.sin(angle1) * aspectRadius;
      const y1 = -Math.cos(angle1) * aspectRadius;
      const x2 = Math.sin(angle2) * aspectRadius;
      const y2 = -Math.cos(angle2) * aspectRadius;

      const aspectColor = ASPECT_COLORS[aspect.type] || '#999';

      g.append('line')
        .attr('x1', x1)
        .attr('y1', y1)
        .attr('x2', x2)
        .attr('y2', y2)
        .attr('stroke', aspectColor)
        .attr('stroke-width', 1)
        .attr('opacity', 0.4)
        .attr('stroke-dasharray', aspect.type === 'opposition' ? '4,4' : aspect.type === 'square' ? '2,2' : null)
        .attr('cursor', 'pointer')
        .on('click', () => {
          haptic.impact('light');
          onAspectClick?.(aspect);
        })
        .on('mouseenter', function (event) {
          d3.select(this).attr('stroke-width', 3).attr('opacity', 1);
          const symbol = ASPECT_SYMBOLS[aspect.type] || '';
          setTooltip({
            content: `${symbol} ${aspect.typeRu}: ${planet1.nameRu} - ${planet2.nameRu} (${aspect.orb.toFixed(1)}°)`,
            x: event.offsetX,
            y: event.offsetY,
          });
        })
        .on('mouseleave', function () {
          d3.select(this).attr('stroke-width', 1).attr('opacity', 0.4);
          setTooltip(null);
        })
        .on('touchstart', function (event) {
          event.preventDefault();
          d3.select(this).attr('stroke-width', 3).attr('opacity', 1);
          // Tooltip removed - touch devices open widget directly
        })
        .on('touchend', function () {
          d3.select(this).attr('stroke-width', 1).attr('opacity', 0.4);
          setTooltip(null);
          // Open widget on touch
          haptic.impact('light');
          onAspectClick?.(aspect);
        });
    });

    // Create shadow filter for planets
    const filter = defs.append('filter')
      .attr('id', 'planet-shadow')
      .attr('height', '200%')
      .attr('width', '200%');

    filter.append('feGaussianBlur')
      .attr('in', 'SourceAlpha')
      .attr('stdDeviation', 2);

    filter.append('feOffset')
      .attr('dx', 0)
      .attr('dy', 1)
      .attr('result', 'offsetblur');

    const feMerge = filter.append('feMerge');
    feMerge.append('feMergeNode');
    feMerge.append('feMergeNode')
      .attr('in', 'SourceGraphic');

    // Draw planets
    Object.entries(chart.planets).forEach(([, planet]) => {
      const angle = (longitudeToAngle(planet.longitude) * Math.PI) / 180;
      const x = Math.sin(angle) * planetRadius;
      const y = -Math.cos(angle) * planetRadius;

      const symbol = PLANET_SYMBOLS[planet.name] || planet.name[0].toUpperCase();

      // Planet glow for retrograde
      if (planet.retrograde) {
        g.append('circle')
          .attr('cx', x)
          .attr('cy', y)
          .attr('r', 28)
          .attr('fill', 'none')
          .attr('stroke', ELEMENT_COLORS.fire)
          .attr('stroke-width', 2)
          .attr('opacity', 0.3)
          .style('filter', 'blur(4px)')
          .attr('pointer-events', 'none');
      }

      // Planet circle background (22px radius = 44px touch target)
      g.append('circle')
        .attr('cx', x)
        .attr('cy', y)
        .attr('r', 22)
        .attr('fill', 'var(--tg-theme-bg-color)')
        .attr('stroke', planet.retrograde ? ELEMENT_COLORS.fire : 'var(--tg-theme-accent-text-color)')
        .attr('stroke-width', planet.retrograde ? 2.5 : 2)
        .style('filter', 'url(#planet-shadow)')
        .attr('cursor', 'pointer')
        .style('transition', 'all 0.2s ease')
        .on('click', () => {
          haptic.impact('medium');
          onPlanetClick?.(planet);
        })
        .on('mouseenter', function (event) {
          d3.select(this)
            .attr('r', 26)
            .attr('stroke-width', planet.retrograde ? 3 : 2.5);
          const retroText = planet.retrograde ? ' ℞' : '';
          const positionText = `${formatDMS(planet.degree)} ${planet.signRu}`;
          const speedText = `Скорость: ${formatSpeed(planet.speed)}`;
          setTooltip({
            content: `${symbol} ${planet.nameRu}${retroText}\n${positionText}\n${speedText} | Дом ${planet.house}`,
            x: event.offsetX,
            y: event.offsetY,
          });
        })
        .on('mouseleave', function () {
          d3.select(this)
            .attr('r', 22)
            .attr('stroke-width', planet.retrograde ? 2.5 : 2);
          setTooltip(null);
        })
        .on('touchstart', function (event) {
          event.preventDefault();
          d3.select(this)
            .attr('r', 26)
            .attr('stroke-width', planet.retrograde ? 3 : 2.5);
          // Tooltip removed - touch devices open widget directly
        })
        .on('touchend', function () {
          d3.select(this)
            .attr('r', 22)
            .attr('stroke-width', planet.retrograde ? 2.5 : 2);
          setTooltip(null);
          // Open widget on touch
          haptic.impact('medium');
          onPlanetClick?.(planet);
        });

      // Planet symbol
      g.append('text')
        .attr('x', x)
        .attr('y', y)
        .attr('text-anchor', 'middle')
        .attr('dominant-baseline', 'central')
        .attr('font-size', '16px')
        .attr('font-weight', '600')
        .attr('fill', 'var(--tg-theme-text-color)')
        .style('text-shadow', '0 1px 2px rgba(0, 0, 0, 0.15)')
        .attr('pointer-events', 'none')
        .text(symbol);

      // Degree label next to planet (superscript style)
      const degreeLabel = Math.floor(planet.degree);
      g.append('text')
        .attr('x', x + 16)
        .attr('y', y - 14)
        .attr('text-anchor', 'start')
        .attr('dominant-baseline', 'central')
        .attr('font-size', '9px')
        .attr('font-weight', '500')
        .attr('fill', 'var(--tg-theme-hint-color)')
        .attr('pointer-events', 'none')
        .text(`${degreeLabel}°`);
    });

    // Draw center circle with gradient
    const centerGradient = defs.append('radialGradient')
      .attr('id', 'center-gradient');

    centerGradient.append('stop')
      .attr('offset', '0%')
      .attr('stop-color', 'var(--tg-theme-secondary-bg-color)')
      .attr('stop-opacity', 0.8);

    centerGradient.append('stop')
      .attr('offset', '100%')
      .attr('stop-color', 'var(--tg-theme-bg-color)')
      .attr('stop-opacity', 0.3);

    g.append('circle')
      .attr('cx', 0)
      .attr('cy', 0)
      .attr('r', innerRadius)
      .attr('fill', 'url(#center-gradient)')
      .attr('stroke', 'var(--tg-theme-hint-color)')
      .attr('stroke-width', 0.5)
      .attr('opacity', 1);

    // Create arrowhead marker for ASC/DSC
    const arrowMarker = defs.append('marker')
      .attr('id', 'arrowhead')
      .attr('markerWidth', '8')
      .attr('markerHeight', '6')
      .attr('refX', '8')
      .attr('refY', '3')
      .attr('orient', 'auto');

    arrowMarker.append('polygon')
      .attr('points', '0 0, 8 3, 0 6')
      .attr('fill', 'var(--tg-theme-accent-text-color)');

    // Draw ASC/MC markers
    const ascAngle = (longitudeToAngle(chart.points.asc?.longitude || 0) * Math.PI) / 180;
    const mcAngle = (longitudeToAngle(chart.points.mc?.longitude || 0) * Math.PI) / 180;
    const dscAngle = ascAngle + Math.PI; // DSC is opposite to ASC

    // ASC marker with arrow pointing left (toward horizon)
    const ascMarkerR = houseRadius + 5;
    const ascX = Math.sin(ascAngle) * ascMarkerR;
    const ascY = -Math.cos(ascAngle) * ascMarkerR;

    const ascGroup = g.append('g');

    // Arrow line for ASC (pointing outward/left on horizon)
    const arrowLength = 18;
    const arrowStartR = ascMarkerR + 14;
    const arrowEndR = arrowStartR + arrowLength;
    ascGroup.append('line')
      .attr('x1', Math.sin(ascAngle) * arrowStartR)
      .attr('y1', -Math.cos(ascAngle) * arrowStartR)
      .attr('x2', Math.sin(ascAngle) * arrowEndR)
      .attr('y2', -Math.cos(ascAngle) * arrowEndR)
      .attr('stroke', 'var(--tg-theme-accent-text-color)')
      .attr('stroke-width', 2)
      .attr('marker-end', 'url(#arrowhead)');

    ascGroup.append('circle')
      .attr('cx', ascX)
      .attr('cy', ascY)
      .attr('r', 12)
      .attr('fill', 'var(--tg-theme-bg-color)')
      .attr('stroke', 'var(--tg-theme-accent-text-color)')
      .attr('stroke-width', 2);

    ascGroup.append('text')
      .attr('x', ascX)
      .attr('y', ascY)
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'central')
      .attr('font-size', '9px')
      .attr('font-weight', 'bold')
      .attr('fill', 'var(--tg-theme-accent-text-color)')
      .text('Asc');

    // DSC marker with arrow pointing right (opposite to ASC)
    const dscX = Math.sin(dscAngle) * ascMarkerR;
    const dscY = -Math.cos(dscAngle) * ascMarkerR;

    const dscGroup = g.append('g');

    // Arrow line for DSC
    dscGroup.append('line')
      .attr('x1', Math.sin(dscAngle) * arrowStartR)
      .attr('y1', -Math.cos(dscAngle) * arrowStartR)
      .attr('x2', Math.sin(dscAngle) * arrowEndR)
      .attr('y2', -Math.cos(dscAngle) * arrowEndR)
      .attr('stroke', 'var(--tg-theme-hint-color)')
      .attr('stroke-width', 1.5)
      .attr('marker-end', 'url(#arrowhead)');

    dscGroup.append('circle')
      .attr('cx', dscX)
      .attr('cy', dscY)
      .attr('r', 12)
      .attr('fill', 'var(--tg-theme-bg-color)')
      .attr('stroke', 'var(--tg-theme-hint-color)')
      .attr('stroke-width', 1.5);

    dscGroup.append('text')
      .attr('x', dscX)
      .attr('y', dscY)
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'central')
      .attr('font-size', '9px')
      .attr('font-weight', 'bold')
      .attr('fill', 'var(--tg-theme-hint-color)')
      .text('Dsc');

    // MC marker with background
    const mcX = Math.sin(mcAngle) * ascMarkerR;
    const mcY = -Math.cos(mcAngle) * ascMarkerR;

    const mcGroup = g.append('g');
    mcGroup.append('circle')
      .attr('cx', mcX)
      .attr('cy', mcY)
      .attr('r', 12)
      .attr('fill', 'var(--tg-theme-bg-color)')
      .attr('stroke', 'var(--tg-theme-accent-text-color)')
      .attr('stroke-width', 2);

    mcGroup.append('text')
      .attr('x', mcX)
      .attr('y', mcY)
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'central')
      .attr('font-size', '9px')
      .attr('font-weight', 'bold')
      .attr('fill', 'var(--tg-theme-accent-text-color)')
      .text('MC');

    // IC marker (opposite to MC)
    const icAngle = mcAngle + Math.PI;
    const icX = Math.sin(icAngle) * ascMarkerR;
    const icY = -Math.cos(icAngle) * ascMarkerR;

    const icGroup = g.append('g');
    icGroup.append('circle')
      .attr('cx', icX)
      .attr('cy', icY)
      .attr('r', 12)
      .attr('fill', 'var(--tg-theme-bg-color)')
      .attr('stroke', 'var(--tg-theme-hint-color)')
      .attr('stroke-width', 1.5);

    icGroup.append('text')
      .attr('x', icX)
      .attr('y', icY)
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'central')
      .attr('font-size', '9px')
      .attr('font-weight', 'bold')
      .attr('fill', 'var(--tg-theme-hint-color)')
      .text('IC');

  }, [chart, computedSize, longitudeToAngle, haptic, onPlanetClick, onAspectClick]);

  return (
    <div ref={containerRef} className={styles.container}>
      <svg ref={svgRef} className={styles.chart} />
      {tooltip && (
        <div
          className={styles.tooltip}
          style={{
            left: tooltip.x + 10,
            top: tooltip.y + 10,
          }}
        >
          {tooltip.content.split('\n').map((line, i) => (
            <div key={i}>{line}</div>
          ))}
        </div>
      )}
    </div>
  );
}

export default ChartWheel;
